import type { ReactNode } from "react";
import type { ExclusionCounts, GroupAnalysisDto } from "@gazelab/shared";
import { EXCLUSION_LABELS, fmtNum, fmtPct } from "../../lib/format.js";

const int = (v: number) => v.toLocaleString("es-MX");
const excludedTotal = (g: GroupAnalysisDto) => Object.values(g.excluded).reduce((x, y) => x + y, 0);

/** Encabezado de columna con el nombre del grupo, su letra y su n. */
function GroupHead({ g, label }: { g: GroupAnalysisDto; label: "A" | "B" }) {
  return (
    <th scope="col" className="px-3 py-2 text-left font-semibold text-slate-800">
      <span className={`mr-1.5 inline-flex h-5 w-5 items-center justify-center rounded-full text-[0.7rem] font-bold text-white ${label === "A" ? "bg-sky-700" : "bg-amber-600"}`}>{label}</span>
      {g.groupName}
    </th>
  );
}

/**
 * Métricas de los dos grupos en una sola tabla comparativa (filas = métricas, columnas = grupos),
 * en lugar de dos listas largas apiladas.
 */
export function GroupCompare({ a, b }: { a: GroupAnalysisDto; b: GroupAnalysisDto }) {
  const rows: Array<{ label: string; hint?: string; get: (g: GroupAnalysisDto) => ReactNode; strong?: boolean }> = [
    { label: "Participantes elegibles (n)", get: (g) => <span className={g.n < 10 ? "text-amber-700" : ""}>{g.n}</span>, strong: true },
    { label: "Edades reales", get: (g) => (g.ageRange ? `${g.ageRange.min}–${g.ageRange.max} años` : "—") },
    { label: "Excluidos", hint: "Detalle por motivo en la pestaña «Exclusiones»", get: (g) => excludedTotal(g) },
    { label: "Muestras estimadas (crudas / válidas)", get: (g) => `${int(g.samples.raw)} / ${int(g.samples.valid)}` },
    { label: "En imagen / fuera de imagen", get: (g) => `${int(g.samples.onImage)} / ${int(g.samples.offImage)} (${fmtPct(g.samples.offImageRatio)} fuera)` },
    { label: "Inválidas", get: (g) => int(g.samples.invalid) },
    { label: "Error de validación medio", get: (g) => `${fmtNum(g.quality.meanValidationErrorPx, 0)} px · ${fmtPct(g.quality.meanValidationErrorRelative)} diag.` },
    { label: "Fracción válida media", get: (g) => fmtPct(g.quality.meanValidSampleRatio) },
    { label: "Muestras por segundo (media)", get: (g) => fmtNum(g.quality.meanSamplesPerSecond, 1) },
    { label: "Cobertura espacial (10×10)", hint: "Celdas visitadas de una rejilla 10×10, promedio por participante", get: (g) => fmtPct(g.meanCoverage) },
    { label: "Sesiones con autorización manual", get: (g) => g.quality.sessionsWithOverride },
  ];
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm" aria-label="Métricas comparadas de los grupos A y B">
        <thead className="border-b border-slate-200 bg-slate-50/70">
          <tr>
            <th scope="col" className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              Métrica
            </th>
            <GroupHead g={a} label="A" />
            <GroupHead g={b} label="B" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.label} className="hover:bg-slate-50/60">
              <th scope="row" className="whitespace-nowrap px-3 py-1 text-left font-normal text-slate-500" title={r.hint}>
                {r.label}
              </th>
              <td className={`px-3 py-1 tabular-nums ${r.strong ? "text-base font-bold" : ""}`}>{r.get(a)}</td>
              <td className={`px-3 py-1 tabular-nums ${r.strong ? "text-base font-bold" : ""}`}>{r.get(b)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Exclusiones por motivo para ambos grupos (se aplican igual a los dos). */
export function ExclusionCompare({ a, b }: { a: GroupAnalysisDto; b: GroupAnalysisDto }) {
  const keys = Object.keys(a.excluded) as Array<keyof ExclusionCounts>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm" aria-label="Exclusiones por motivo">
        <thead className="border-b border-slate-200 bg-slate-50/70">
          <tr>
            <th scope="col" className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
              Motivo
            </th>
            <GroupHead g={a} label="A" />
            <GroupHead g={b} label="B" />
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {keys.map((k) => (
            <tr key={k}>
              <th scope="row" className="px-3 py-1 text-left font-normal text-slate-600">
                {EXCLUSION_LABELS[k]}
              </th>
              <td className="px-3 py-1 tabular-nums">{a.excluded[k]}</td>
              <td className="px-3 py-1 tabular-nums">{b.excluded[k]}</td>
            </tr>
          ))}
          <tr className="font-semibold">
            <th scope="row" className="px-3 py-1 text-left">
              Total
            </th>
            <td className="px-3 py-1 tabular-nums">{excludedTotal(a)}</td>
            <td className="px-3 py-1 tabular-nums">{excludedTotal(b)}</td>
          </tr>
        </tbody>
      </table>
      <p className="mt-2 px-3 text-xs text-slate-500">Cada sesión cuenta en el primer motivo que la excluye. Las reglas se aplican igual a ambos grupos.</p>
    </div>
  );
}
